import type {AppState} from '@auth0/auth0-react';

/**
 * The SDK's default `onRedirectCallback` only rewrites the URL bar via raw
 * `history.replaceState`, which a data router (`createBrowserRouter`) doesn't react to —
 * routing back to `appState.returnTo` requires going through the router's own `navigate`.
 * `returnTo` is a browser path, so the router's `basename` comes off before `navigate` adds it back.
 */
export function createRouterRedirectCallback(
  navigate: (to: string, options?: {replace?: boolean; flushSync?: boolean}) => unknown,
  {basename}: {basename?: string} = {}
): (appState?: AppState) => void {
  return appState => {
    // A transition would lose to the gate's urgent signed-in render, letting the `/` redirect run first.
    void navigate(stripBasename(appState?.returnTo ?? '/', basename), {
      replace: true,
      flushSync: true
    });
  };
}

function stripBasename(path: string, basename: string | undefined): string {
  if (!basename || basename === '/' || !path.startsWith(basename)) return path;
  const rest = path.slice(basename.length);
  if (rest === '') return '/';
  if (rest.startsWith('/')) return rest;
  return /^[?#]/.test(rest) ? `/${rest}` : path;
}
